import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@viastud/ui/button'
import { Checkbox } from '@viastud/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@viastud/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@viastud/ui/form'
import { useToast } from '@viastud/ui/hooks/use-toast'
import { Input } from '@viastud/ui/input'
import { trpc } from '@viastud/ui/lib/trpc'
import { PasswordInput } from '@viastud/ui/password-input'
import { PhoneInput } from '@viastud/ui/phone-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viastud/ui/select'
import { InlinePasswordPopover } from '@viastud/ui/shared/inline-password-popover'
import { OtpInput } from '@viastud/ui/shared/otp-input'
import { useSmsCaptcha } from '@viastud/ui/shared/sms-captcha'
import { GradeEnum, type RegisterUser, registerUserSchema } from '@viastud/utils'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useShallow } from 'zustand/shallow'

import { OnboardingCard } from '@/components/onboarding/onboarding-card.tsx'
import { gtmService } from '@/services/gtm-service.ts'
import { useAuthStore } from '@/store/auth.store.ts'
import { useOnBoardingStore } from '@/store/onboarding.store.ts'

import { CGV } from '../cgv.tsx'

export const OnboardingCreateAccount: React.FC = () => {
  const { requestToken, captchaDialog } = useSmsCaptcha()
  const passwordAnchorRef = React.useRef<HTMLDivElement>(null)
  const [isPasswordOpen, setIsPasswordOpen] = useState(false)
  const [, setIsPhoneVerified] = useState(false)
  const [isCguModalOpen, setIsCguModalOpen] = useState(false)
  const [isOtpStep, setIsOtpStep] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { toast } = useToast()
  const { grade, role, parentChildren } = useOnBoardingStore()

  const { mutateAsync: sendSMSCodeMutate } = trpc.userAuth.sendPhoneVerificationCode.useMutation()
  const { mutateAsync: verifySMSCodeMutate } = trpc.userAuth.verifyPhoneCode.useMutation()
  const { mutateAsync: registerUserMutate } = trpc.userAuth.register.useMutation()
  const { mutateAsync: loginMutation } = trpc.userAuth.login.useMutation()
  const { mutateAsync: userExistMutation } = trpc.userAuth.userExist.useMutation()

  const navigate = useNavigate({ from: '/' })
  const { updateAuth } = useAuthStore(
    useShallow((state) => ({
      storedRole: state.role,
      updateAuth: state.updateAuth,
    }))
  )
  const sendSMSCode = async (phoneNumber: string) => {
    // Prevent sending SMS if phone already used by a child (avoid SMS cost)
    const normalizedPhone = phoneNumber?.replace(/\s+/g, '').toLowerCase()
    const childPhoneConflict = parentChildren
      ?.filter((c) => c.type === 'NEW_CHILD')
      ?.some((c) => c.phoneNumber?.replace(/\s+/g, '').toLowerCase() === normalizedPhone)

    if (childPhoneConflict) {
      toast({
        title: 'Téléphone déjà utilisé',
        description: 'Un enfant utilise déjà ce numéro de téléphone.',
        variant: 'destructive',
      })
      return
    }
    try {
      await sendSMSCodeMutate({
        phoneNumber,
        captchaToken: await requestToken(),
      })
      setIsPhoneVerified(false)
      setIsOtpStep(true)
      toast({
        title: 'Un code a été envoyé par SMS',
      })
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Une erreur est survenue',
          description: error.message,
          variant: 'destructive',
        })
      }
    }
  }
  const verifySMSCode = async (phoneNumber: string, otp: string) => {
    try {
      const result = await verifySMSCodeMutate({
        phoneNumber,
        code: otp,
      })
      if (result?.success) {
        setIsPhoneVerified(true)
        toast({
          title: 'Numéro de téléphone vérifié avec succès',
        })
        return true
      }
    } catch {
      toast({
        title: 'Le code est invalide, veuillez réessayer',
        variant: 'destructive',
      })
    }
    return false
  }

  const createUserForm = useForm<RegisterUser>({
    resolver: zodResolver(registerUserSchema),
    mode: 'onChange',
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      grade: grade,
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      cgu: false,
    },
  })

  const onSubmit = async (data: RegisterUser) => {
    // 1. check user email
    try {
      const userExist = await userExistMutation({ email: data.email.toLowerCase() })
      if (userExist.exist) {
        toast({
          title: 'Email déjà utilisé',
          description: 'Cette adresse email est déjà associée à un compte.',
          variant: 'destructive',
        })
        return
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Une erreur est survenue',
          description: error.message || 'Une erreur inconnue est survenue',
          variant: 'destructive',
        })
      }
      return
    }

    // 2. send sms code
    if (!isOtpStep) {
      void sendSMSCode(data.phoneNumber)
      return
    }

    if (!otpCode || otpCode.length < 6) {
      toast({
        title: 'Veuillez saisir le code de vérification',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)

    // 3. verify sms code
    try {
      const verified = await verifySMSCode(data.phoneNumber, otpCode)
      if (!verified) {
        setIsSubmitting(false)
        return
      }

      // Register
      if (role === 'STUDENT') {
        await registerUserMutate({
          lastName: data.lastname,
          firstName: data.firstname,
          email: data.email.toLowerCase(),
          phoneNumber: data.phoneNumber,
          role: role,
          grade: data.grade,
          interestedIn: ['MATHS'],
          password: data.password,
        })
      } else if (role === 'PARENT') {
        // Prevent parent using same email or phone as a child
        const normalizedParentEmail = data.email.toLowerCase()
        const normalizedParentPhone = data.phoneNumber?.replace(/\s+/g, '')

        const parentChildEmailConflict = parentChildren?.some(
          (c) => 'email' in c && c.email?.toLowerCase() === normalizedParentEmail
        )

        const parentChildPhoneConflict = parentChildren
          ?.filter((c) => c.type === 'NEW_CHILD')
          ?.some((c) => c.phoneNumber?.replace(/\s+/g, '').toLowerCase() === normalizedParentPhone)

        if (parentChildEmailConflict) {
          toast({
            title: 'Email déjà utilisé',
            description: 'Un enfant utilise déjà cette adresse email.',
            variant: 'destructive',
          })
          return
        }

        if (parentChildPhoneConflict) {
          toast({
            title: 'Téléphone déjà utilisé',
            description: 'Un enfant utilise déjà ce numéro de téléphone.',
            variant: 'destructive',
          })
          return
        }

        await registerUserMutate({
          lastName: data.lastname,
          firstName: data.firstname,
          email: data.email.toLowerCase(),
          phoneNumber: data.phoneNumber,
          role: role,
          interestedIn: ['MATHS'],
          parentChildrenNew: parentChildren
            ?.filter((c) => c.type === 'NEW_CHILD')
            ?.map((c) => ({
              firstName: c.firstName,
              lastName: c.lastName,
              email: c.email.toLowerCase(),
              phoneNumber: c.phoneNumber,
              grade: c.grade,
              phoneVerified: c.phoneVerified,
            })),
          parentChildrenImported: parentChildren
            ?.filter((c) => c.type === 'IMPORTED_CHILD')
            ?.map((c) => ({
              email: c.email.toLowerCase(),
            })),
          password: data.password,
        })
      } else {
        toast({
          title: 'Vous devez selectionner un Role',
          variant: 'destructive',
        })
      }

      toast({
        title: 'Inscription réussite',
      })
      gtmService.trackRegistrationSuccess()

      // Login
      const { user, role: userRole } = await loginMutation({
        email: data.email.toLowerCase(),
        password: data.password,
      })

      // Save store
      updateAuth({
        user,
        role: userRole,
        isAuthenticated: true,
      })

      // Redirection
      if (userRole === 'PARENT') {
        // doing this because the _student file trigger first and I don't know how to avoid this

        window.location.href = '/?onboarded=true'
      } else {
        void navigate({ to: '/', search: { onboarded: true } })
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Une erreur est survenue',
          description: error.message || 'Une erreur inconnue est survenue',
          variant: 'destructive',
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <OnboardingCard>
      {captchaDialog}
      <div className="flex h-full flex-col">
        <div className="text-center">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Créez votre compte</h1>
        </div>

        <Form {...createUserForm}>
          <form onSubmit={createUserForm.handleSubmit(onSubmit)}>
            <div className="flex flex-col items-start gap-4 self-stretch py-4">
              <div className="flex w-full gap-x-4">
                <FormField
                  name="firstname"
                  control={createUserForm.control}
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">Prénom</FormLabel>
                      <FormControl>
                        <Input className="w-full" placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="lastname"
                  control={createUserForm.control}
                  render={({ field }) => (
                    <FormItem className="flex w-full flex-col">
                      <FormLabel className="text-sm font-medium text-gray-700">Nom</FormLabel>
                      <FormControl>
                        <Input className="w-full" placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                name="email"
                control={createUserForm.control}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Email</FormLabel>
                    <FormControl>
                      <Input className="w-full" placeholder="john.doe@email.fr" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="grade"
                control={createUserForm.control}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Niveau</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full rounded-md border-gray-200">
                          <SelectValue placeholder="Sélectionnez votre niveau" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(GradeEnum).map(([value, label]) =>
                          value !== 'SIXIEME' && value !== 'CINQUIEME' && value !== 'QUATRIEME' ? (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ) : null
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="password"
                control={createUserForm.control}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Mot de passe
                    </FormLabel>
                    <FormControl>
                      <div ref={passwordAnchorRef} className="relative w-full">
                        <PasswordInput
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Entrez votre mot de passe"
                          required
                          onFocus={() => {
                            setIsPasswordOpen(true)
                          }}
                          onBlur={() => {
                            setIsPasswordOpen(false)
                          }}
                        />
                        <InlinePasswordPopover
                          anchorRef={passwordAnchorRef}
                          isOpen={isPasswordOpen}
                          password={field.value}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="confirmPassword"
                control={createUserForm.control}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Répetez le mot de passe
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Répetez le mot de passe"
                        required
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="phoneNumber"
                control={createUserForm.control}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormLabel className="text-sm font-medium text-gray-700">Téléphone</FormLabel>

                    <FormControl>
                      <PhoneInput
                        className="w-full"
                        placeholder="Numéro de téléphone"
                        {...field}
                        disabled={isOtpStep}
                        onChange={(value: string) => {
                          field.onChange(value)
                          setIsPhoneVerified(false)
                        }}
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />

              {isOtpStep && (
                <div className="flex w-full flex-col gap-2 rounded-lg border border-indigo-100 bg-indigo-50/30 p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Confirmer le numéro</p>
                    <button
                      type="button"
                      className="text-xs text-indigo-600 underline"
                      onClick={() => {
                        setIsOtpStep(false)
                      }}
                    >
                      Modifier le numéro
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Entrez le code envoyé au {createUserForm.getValues('phoneNumber')}
                  </p>
                  <OtpInput
                    value={otpCode}
                    onChange={setOtpCode}
                    onResendOtp={() => sendSMSCode(createUserForm.getValues('phoneNumber'))}
                  />
                </div>
              )}
              <FormField
                name="cgu"
                control={createUserForm.control}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col">
                    <FormControl>
                      <div className="flex items-center gap-x-2">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        <p>
                          Vous acceptez les{' '}
                          <span
                            className="cursor-pointer underline"
                            onClick={() => {
                              setIsCguModalOpen(true)
                            }}
                          >
                            CGU
                          </span>
                          .
                        </p>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <Button
              variant="default"
              className="w-full"
              type="submit"
              disabled={!createUserForm.formState.isValid || isSubmitting}
            >
              {isOtpStep ? 'Confirmer et créer mon compte' : 'Créer mon compte'}
            </Button>
          </form>
        </Form>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        <p className="text-sm text-gray-600">Vous avez déjà un compte ?</p>
        <Link to="/login">
          <p className="text-sm font-semibold text-blue-800">Se connecter</p>
        </Link>
      </div>

      <Dialog open={isCguModalOpen} onOpenChange={setIsCguModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conditions Générales d&#39;Utilisation</DialogTitle>
            <DialogDescription>
              <CGV />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </OnboardingCard>
  )
}
